<template>
  <div class="px-10">
    <v-expansion-panels
      v-model="panel"
      multiple
      class="elevation-2"
    >
      <v-expansion-panel>
        <v-expansion-panel-header class="font-calibri font-16 font-weight-bold border-bottom-light-grey accordion-max-height">
          {{ section }} 
          <employee-profile-general-info-dependent-dialog-create
            v-if="panel.length > 0 && $hasPermissionTo(['create dependents']) && !isMyProfile && !isResigned"
            :employee-uid="employeeUid"
            @created="loadDependents"
          />
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :headers="headers"
            :items="dependents"
            hide-default-footer
            dense
            class="border mt-4"
          >
            <template v-slot:item.name="{ item }">
              <v-skeleton-loader
                v-if="loading"
                type="list-item"
                class="loading-bar"
              />  
              <v-tooltip
                v-else
                bottom
              >
                <template v-slot:activator="{ on: tooltip }">
                  <v-icon
                    x-small
                    color="info"
                    v-on="{ ...tooltip }"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <div>Citizenship: <b>{{ item.is_citizen ? 'Yes':'No' }}</b></div>
                <div>Birth Date: <b>{{ $moment(item.birth_date).format('DD MMM YYYY') }}</b></div>
                <div>Disabled: <b>{{ item.is_disabled ? 'Yes':'No' }}</b></div>
                <div>Income Tax No.:<b>{{ item.income_tax? item.income_tax:'N/A' }}</b></div>
              </v-tooltip>
              <span v-if="!loading">
                {{ item.name }}
              </span>
            </template>

            <template v-slot:item.ic_passport="{ item }">
              <v-skeleton-loader
                v-if="loading"
                type="list-item"
                class="loading-bar"
              />  
              <v-tooltip
                v-else
                bottom
              >
                <template v-slot:activator="{ on: tooltip }">
                  <v-icon
                    x-small
                    color="info"
                    v-on="{ ...tooltip }"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <div><b>{{ !item.ic ? 'Passport No.' :'Identity Card No.' }}</b></div>
              </v-tooltip>
              <span v-if="!loading">
                {{ !item.ic ? item.passport_no : item.ic }}
              </span>
            </template>

            <template v-slot:item.relationship.name="{ item }">
              <div>
                <v-skeleton-loader
                  v-if="loading"
                  type="list-item"
                />  
                <span v-else>
                  {{ item.relationship.name === 'Spouse' && employeeMaritalStatus === 'Divorced' ? item.relationship.name + ' (' + employeeMaritalStatus + ')' : item.relationship.name }}
                </span>
              </div>
            </template>

            <template v-slot:item.lookup_gender.description2="{ item }">
              <div>
                <v-skeleton-loader
                  v-if="loading"
                  type="list-item"
                />  
                <span v-else>
                  {{ ((item || {}).lookup_gender || {}).description2 }}
                </span>
              </div>
            </template>

            <template v-slot:item.relation_status.name="{ item }">
              <div>
                <v-skeleton-loader
                  v-if="loading"
                  type="list-item"
                />  
                <span v-else>
                  {{ item.relation_status.name }}
                </span>
              </div>
            </template>
            
            <template v-slot:item.work="{ item }">
              <v-skeleton-loader
                v-if="loading"
                type="list-item"
                class="loading-bar"
              />  
              <v-tooltip
                v-else
                bottom
              >
                <template v-slot:activator="{ on: tooltip }">
                  <v-icon
                    x-small
                    color="info"
                    v-on="{ ...tooltip }"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <div><b>{{ item.occupation ? item.occupation:'N/A' }}</b></div>
              </v-tooltip>
              <span v-if="!loading">
                {{ item.company_name?(item.company_name=='null'?'N/A':item.company_name):'N/A' }}
              </span>
            </template>

            <template v-slot:item.is_tax_relief="{ item }">
              <v-skeleton-loader
                v-if="loading"
                type="list-item"
                class="loading-bar"
              /> 
              <div v-else>
                <v-icon 
                  v-if="item.is_tax_relief === 1" 
                  color="hrissuccess"
                >
                  mdi-check-outline
                </v-icon>
                <v-icon 
                  v-else 
                  color="error"
                >
                  mdi-close-outline
                </v-icon> 
                <span
                  v-if="item.is_tax_relief === 1"
                  class="font-weight-bold"
                >&nbsp;&nbsp;&nbsp;{{ item.tax_relief_percentage }}%</span>   
              </div>
            </template>

            <template v-slot:item.action="{ item }">
              <employee-profile-general-info-dependent-dialog-view
                v-if="$hasPermissionTo(['view dependents documents']) && !loading && !isMyProfile"
                :dependent="item"
                :is-resigned="isResigned"
                @updated="loadDependents"
              />
              <employee-profile-general-info-dependent-dialog-edit
                v-if="$hasPermissionTo(['update dependents']) && !loading && !isMyProfile && !isResigned"
                :dependent="item"
                @updated="loadDependents"
              />
              <employee-profile-general-info-dependent-dialog-delete
                v-if="$hasPermissionTo(['delete dependents']) && !loading && !isMyProfile && !isResigned"
                :dependent="item"
                @deleted="loadDependents"
              />
              <employee-profile-general-info-dependent-history-dialog-list-histories
                v-if="$hasPermissionTo(['view hris employees']) && !loading && !isMyProfile && !isResigned && (item.relationship.name === 'Spouse' || item.relationship.name === 'Children')"
                :dependent-histories="item.dependent_histories"
                :dependent-id="item.id"
                :dependent-name="item.name"
                :dependent-relation="item.relationship"
                :is-resigned="isResigned"
                @updated="loadDependents"
              />
            </template>  
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  export default {
    components: {
      EmployeeProfileGeneralInfoDependentDialogCreate: () => import('@/components/hris/employee/profile/general-info/dependent/DialogCreate'),
      EmployeeProfileGeneralInfoDependentDialogEdit: () => import('@/components/hris/employee/profile/general-info/dependent/DialogEdit'),
      EmployeeProfileGeneralInfoDependentDialogView: () => import('@/components/hris/employee/profile/general-info/dependent/DialogView'),
      EmployeeProfileGeneralInfoDependentDialogDelete: () => import('@/components/hris/employee/profile/general-info/dependent/DialogDelete'),
      EmployeeProfileGeneralInfoDependentHistoryDialogListHistories: () => import('@/components/hris/employee/profile/general-info/dependent/history/DialogListHistories'),
    },
    props: {
      employeeUid: {
        type: String,
        required: true,
      },
      employeeMaritalStatus: {
        type: String,
        default: null,
      },
      isResigned: {
        type: Boolean,
        required: true,
      },
    },
    data () {
      return {
        section: 'Immediate Families',
        dependents: [],
        headers: [
          { 
            text: 'Name', 
            align: 'left',
            sortable: false,
            value: 'name',
          },
          {
            text: 'IC/Passport No.',
            align: 'left',
            sortable: false,
            value: 'ic_passport',
          },
          { 
            text: 'Relationship',
            align: 'center',
            sortable: false,
            value: 'relationship.name',
          },
          { 
            text: 'Gender',
            align: 'center',
            sortable: false,
            value: 'lookup_gender.description2',
          },
          { 
            text: 'Relation Status',
            align: 'center',
            sortable: false,
            value: 'relation_status.name', 
          },
          { 
            text: 'Workplace',
            align: 'left',
            sortable: false,
            value: 'work', 
          },
          { 
            text: 'Tax Relief',
            align: 'left',
            sortable: false,
            value: 'is_tax_relief', 
          },
          { 
            text: '',
            sortable: false,
            value: 'action', 
          },
        ],
        loading: false,
        panel: [0],
      }
    },
    computed: {
      ...mapState({ isCurrentUser: 'isMyProfile' }),
      isMyProfile () {
        return this.$store.state.isMyProfile
      },
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (newVal, oldVal) {
          if (!_.isEmpty(newVal)) {
            this.loadDependents()
          }
        },
      },
    },
  
    methods: {
      loadDependents () {
        this.loading = true

        this.$api.hrisDependents().list(this.employeeUid)
          .then(res => {
            this.dependents = res.data

            this.$emit('refresh')
            this.loading = false
          })
      },
    },
  }
</script>

<style scoped>
.font-calibri{
  font-family: Calibri !important;
}

.font-16{
  font-size:16px;
}

.border-bottom-light-grey{
  border-bottom:1px solid #e6e6e6;
}

.accordion-max-height{
  max-height: 36px !important;
}

.border{
  border:1px solid #e6e6e6;
}

.loading-bar >>> .v-skeleton-loader__list-item.v-skeleton-loader__bone{
  padding-left:0px
}
</style>
